// Módulo Productos — 4.1: "CRUD de productos, variantes, imágenes, SEO por
// producto". Alcance MVP (ver nota de api/productos.js): sin variantes.
//
// El modal de imágenes solo tiene sentido con un producto ya existente
// (producto_imagen.producto_id no es nullable) — por eso, al crear un
// producto nuevo, el formulario NO se cierra: se recarga en modo edición
// sobre el producto recién creado, para que la sección de imágenes
// aparezca de inmediato sin obligar a reabrir el modal.

import { crearTabla, mostrarToast, iconoCandado, iconoEliminar } from '@plataforma/ui';
import {
  debounce,
  esSlugValido,
  escapeAttr,
  escaparHtml,
  formatearMoneda,
  generarSlugSugerido,
  mensajeErrorAmigable,
  tieneFeature,
} from '@plataforma/utils';

import { listarCategorias } from '../api/categorias.js';
import {
  agregarImagenProducto,
  eliminarImagenProducto,
  marcarImagenPrincipal,
} from '../api/producto-imagenes.js';
import {
  agregarProductoRelacionado,
  eliminarProductoRelacionado,
  listarProductosRelacionados,
} from '../api/productos-relacionados.js';
import {
  actualizarProducto,
  actualizarSeoProducto,
  asignarCategoriasProducto,
  crearProducto,
  eliminarProducto,
  listarProductos,
  obtenerProducto,
} from '../api/productos.js';
import {
  aplicarEstadoBorrador,
  conectarBorradorDeFormulario,
  crearGestorBorradorFormulario,
} from '../borradores.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

const ETIQUETA_ESTADO = { borrador: 'Borrador', publicado: 'Publicado', archivado: 'Archivado' };
const TONO_ESTADO = { borrador: 'neutral', publicado: 'exito', archivado: 'alerta' };

export async function vistaProductos(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/productos');
  contenido.innerHTML = `
    <div class="pf-fila pf-fila--separada" style="margin-bottom: 16px;">
      <h1>Productos</h1>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-producto">
        Nuevo producto
      </button>
    </div>

    <div class="pf-fila" style="margin-bottom: 16px; gap: 12px;">
      <div class="pf-campo" style="margin: 0; min-width: 200px;">
        <label for="pf-filtro-estado">Estado</label>
        <select id="pf-filtro-estado">
          <option value="">Todos</option>
          <option value="borrador">Borrador</option>
          <option value="publicado">Publicado</option>
          <option value="archivado">Archivado</option>
        </select>
      </div>
      <div class="pf-campo" style="margin: 0; flex: 1; min-width: 200px;">
        <label for="pf-filtro-busqueda">Buscar</label>
        <input type="search" id="pf-filtro-busqueda" placeholder="Nombre del producto…" />
      </div>
    </div>

    <div id="pf-productos-tabla"><p class="pf-texto-suave">Cargando…</p></div>

    <pf-modal titulo="Nuevo producto" id="pf-modal-producto"></pf-modal>
    <pf-modal titulo="Productos relacionados" id="pf-modal-relacionados"></pf-modal>
  `;

  const filtroEstado = contenido.querySelector('#pf-filtro-estado');
  const filtroBusqueda = contenido.querySelector('#pf-filtro-busqueda');
  const aplicarFiltros = () =>
    cargarTabla(contenido, { estado: filtroEstado.value, busqueda: filtroBusqueda.value });
  filtroEstado.addEventListener('change', aplicarFiltros);
  filtroBusqueda.addEventListener('input', debounce(aplicarFiltros, 300));

  await cargarTabla(contenido, {});

  const modal = contenido.querySelector('#pf-modal-producto');
  contenido.querySelector('#pf-btn-nuevo-producto').addEventListener('click', async () => {
    modal.setAttribute('titulo', 'Nuevo producto');
    await montarFormulario(modal, null, () =>
      cargarTabla(contenido, { estado: filtroEstado.value, busqueda: filtroBusqueda.value }),
    );
    modal.abrir();
  });
}

async function cargarTabla(contenido, filtros) {
  const destino = contenido.querySelector('#pf-productos-tabla');
  const { data, error } = await listarProductos(filtros);

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">No se pudieron cargar los productos.</p>`;
    return;
  }

  const modal = contenido.querySelector('#pf-modal-producto');

  const tabla = crearTabla({
    filaVacia: 'No hay productos que coincidan. Crea el primero con el botón de arriba.',
    columnas: [
      {
        titulo: 'Imagen',
        render: (fila) => {
          const principal = fila.imagenes?.find((i) => i.es_principal) ?? fila.imagenes?.[0];
          if (!principal) return '—';
          const img = document.createElement('img');
          img.src = principal.url;
          img.alt = '';
          img.style.cssText = 'width:40px;height:40px;object-fit:cover;border-radius:6px;';
          return img;
        },
      },
      { clave: 'nombre', titulo: 'Nombre' },
      { titulo: 'Precio', render: (fila) => formatearMoneda(fila.precio) },
      {
        titulo: 'Estado',
        render: (fila) => {
          const badge = document.createElement('pf-badge-estado');
          badge.setAttribute('tono', TONO_ESTADO[fila.estado] ?? 'neutral');
          badge.textContent = ETIQUETA_ESTADO[fila.estado] ?? fila.estado;
          return badge;
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => crearAccionesFila(fila, modal, () => cargarTabla(contenido, {})),
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function crearAccionesFila(producto, modal, alTerminar) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-fila';

  const botonEditar = document.createElement('button');
  botonEditar.type = 'button';
  botonEditar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonEditar.textContent = 'Editar';
  botonEditar.addEventListener('click', async () => {
    modal.setAttribute('titulo', `Editar: ${producto.nombre}`);
    await montarFormulario(modal, producto.id, alTerminar);
    modal.abrir();
  });

  const botonEliminar = document.createElement('button');
  botonEliminar.type = 'button';
  botonEliminar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
  botonEliminar.textContent = 'Eliminar';
  botonEliminar.addEventListener('click', async () => {
    if (!window.confirm(`¿Eliminar "${producto.nombre}"? Dejará de mostrarse en tu tienda.`))
      return;
    botonEliminar.disabled = true;
    const { error } = await eliminarProducto(producto.id);
    if (error) {
      mostrarToast('No se pudo eliminar el producto.', { tono: 'peligro' });
      botonEliminar.disabled = false;
      return;
    }
    mostrarToast('Producto eliminado.', { tono: 'exito' });
    await alTerminar();
  });

  contenedor.append(botonEditar, botonEliminar);

  const empresa = obtenerEstadoSesion().empresa ?? {};
  const tieneVariantes = tieneFeature(empresa, 'variantes');

  const botonVariantes = document.createElement('button');
  botonVariantes.type = 'button';
  botonVariantes.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  if (tieneVariantes) {
    botonVariantes.textContent = 'Variantes';
  } else {
    botonVariantes.innerHTML = `Variantes <span class="pf-premium-indicador">${iconoCandado()}</span>`;
  }
  if (!tieneVariantes) {
    botonVariantes.title = 'Disponible desde el plan Medio';
    botonVariantes.addEventListener('click', () => {
      if (
        window.confirm(
          'La funcionalidad Variantes está disponible desde el plan Medio.\n\n¿Deseas ver los planes?',
        )
      ) {
        window.location.hash = '#/planes';
      }
    });
  } else {
    botonVariantes.addEventListener('click', () => {
      window.location.hash = `#/variantes?producto_id=${producto.id}`;
    });
  }
  contenedor.appendChild(botonVariantes);

  const tieneRelacionados = tieneFeature(empresa, 'productos_relacionados');

  const botonRelacionados = document.createElement('button');
  botonRelacionados.type = 'button';
  botonRelacionados.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  if (tieneRelacionados) {
    botonRelacionados.textContent = 'Relacionados';
  } else {
    botonRelacionados.innerHTML = `Relacionados <span class="pf-premium-indicador">${iconoCandado()}</span>`;
  }

  const modalRelacionados = document.getElementById('pf-modal-relacionados');

  if (!tieneRelacionados) {
    botonRelacionados.title = 'Disponible desde el plan Medio';
    botonRelacionados.addEventListener('click', () => {
      if (modalRelacionados) {
        modalRelacionados.setAttribute('titulo', 'Productos relacionados');
        modalRelacionados.innerHTML = `
          <div class="pf-campo__error" style="padding: 16px; margin-bottom: 16px; display: flex; align-items: flex-start; gap: 12px;">
            <span style="color: var(--pf-color-premium); flex-shrink: 0; margin-top: 2px;">${iconoCandado()}</span>
            <div>
              <strong>Productos Relacionados</strong> — función bloqueada<br>
              Disponible desde el plan <strong>Medio</strong>.<br>
              Recomienda otros productos de tu catálogo a tus clientes.<br>
              <button type="button" class="pf-boton pf-boton--premium" id="pf-btn-upgrade-rel" style="margin-top: 12px;">
                Mejorar plan
              </button>
            </div>
          </div>
        `;
        modalRelacionados.abrir();
        const btnUpgrade = modalRelacionados.querySelector('#pf-btn-upgrade-rel');
        if (btnUpgrade) {
          btnUpgrade.addEventListener('click', () => {
            modalRelacionados.cerrar();
            window.location.hash = '#/planes';
          });
        }
      }
    });
  } else {
    botonRelacionados.addEventListener('click', async () => {
      if (modalRelacionados) {
        await abrirModalRelacionados(modalRelacionados, producto);
        modalRelacionados.abrir();
      }
    });
  }
  contenedor.appendChild(botonRelacionados);

  return contenedor;
}

/** Campos del formulario de producto que se persisten como borrador. */
function leerDatosFormularioProducto(form) {
  const valor = (selector) => form.querySelector(selector)?.value ?? '';
  const marcado = (selector) => form.querySelector(selector)?.checked ?? false;
  return {
    nombre: valor('#pf-prod-nombre'),
    slug: valor('#pf-prod-slug'),
    precio: valor('#pf-prod-precio'),
    precio_anterior: valor('#pf-prod-precio-anterior'),
    descripcion_corta: valor('#pf-prod-descripcion-corta'),
    descripcion_larga: valor('#pf-prod-descripcion-larga'),
    estado: valor('#pf-prod-estado'),
    es_destacado: marcado('#pf-prod-destacado'),
    es_nuevo: form.querySelector('#pf-prod-nuevo') ? marcado('#pf-prod-nuevo') : null,
    es_oferta: form.querySelector('#pf-prod-oferta') ? marcado('#pf-prod-oferta') : null,
    categorias: Array.from(form.querySelectorAll('.pf-prod-categoria:checked')).map(
      (el) => el.value,
    ),
    categoria_principal: valor('#pf-prod-categoria-principal'),
    seo_titulo: valor('#pf-prod-seo-titulo'),
    seo_descripcion: valor('#pf-prod-seo-descripcion'),
  };
}

/** Aplica un borrador sobre el formulario, solo sobre las claves presentes. */
function aplicarDatosFormularioProducto(form, datos) {
  const setValor = (selector, valorDato) => {
    const el = form.querySelector(selector);
    if (el && valorDato !== undefined && valorDato !== null) el.value = valorDato;
  };
  const setMarcado = (selector, valorDato) => {
    const el = form.querySelector(selector);
    if (el && typeof valorDato === 'boolean') el.checked = valorDato;
  };

  setValor('#pf-prod-nombre', datos.nombre);
  setValor('#pf-prod-slug', datos.slug);
  setValor('#pf-prod-precio', datos.precio);
  setValor('#pf-prod-precio-anterior', datos.precio_anterior);
  setValor('#pf-prod-descripcion-corta', datos.descripcion_corta);
  setValor('#pf-prod-descripcion-larga', datos.descripcion_larga);
  setValor('#pf-prod-estado', datos.estado);
  setMarcado('#pf-prod-destacado', datos.es_destacado);
  setMarcado('#pf-prod-nuevo', datos.es_nuevo);
  setMarcado('#pf-prod-oferta', datos.es_oferta);
  setValor('#pf-prod-categoria-principal', datos.categoria_principal);
  setValor('#pf-prod-seo-titulo', datos.seo_titulo);
  setValor('#pf-prod-seo-descripcion', datos.seo_descripcion);

  if (Array.isArray(datos.categorias)) {
    for (const checkbox of form.querySelectorAll('.pf-prod-categoria')) {
      checkbox.checked = datos.categorias.includes(checkbox.value);
    }
  }
}

/**
 * @param {HTMLElement} modal
 * @param {string | null} productoId - null para alta
 * @param {() => Promise<void>} alGuardar
 */
async function montarFormulario(modal, productoId, alGuardar) {
  const [{ data: categorias }, productoResultado] = await Promise.all([
    listarCategorias(),
    productoId ? obtenerProducto(productoId) : Promise.resolve({ data: null, error: null }),
  ]);

  if (productoId && productoResultado.error) {
    mostrarToast('No se pudo cargar el producto.', { tono: 'peligro' });
    return;
  }
  const producto = productoResultado.data;
  const categoriaIdsAsignadas = new Set((producto?.categorias ?? []).map((c) => c.categoria_id));
  const categoriaPrincipalId =
    producto?.categorias?.find((c) => c.es_principal)?.categoria_id ?? null;

  const empresa = obtenerEstadoSesion().empresa ?? {};
  const tieneEtiquetas = tieneFeature(empresa, 'etiquetas_producto');

  modal.innerHTML = `
    <form id="pf-form-producto" novalidate>
      <p class="pf-texto-suave" id="pf-prod-borrador-aviso" hidden style="margin-top:0;">
        Recuperamos un borrador sin guardar.
        <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-prod-borrador-descartar">Descartar</button>
      </p>
      <div class="pf-campo">
        <label for="pf-prod-nombre">Nombre</label>
        <input type="text" id="pf-prod-nombre" required value="${escapeAttr(producto?.nombre ?? '')}" />
      </div>

      <div class="pf-campo">
        <label for="pf-prod-slug">Slug</label>
        <input type="text" id="pf-prod-slug" required value="${escapeAttr(producto?.slug ?? '')}" />
      </div>

      <div class="pf-fila" style="gap: 12px;">
        <div class="pf-campo" style="flex: 1;">
          <label for="pf-prod-precio">Precio (S/)</label>
          <input type="number" id="pf-prod-precio" min="0" step="0.01" required value="${producto?.precio ?? ''}" />
        </div>
        <div class="pf-campo" style="flex: 1;">
          <label for="pf-prod-precio-anterior">Precio anterior (opcional)</label>
          <input type="number" id="pf-prod-precio-anterior" min="0" step="0.01" value="${producto?.precio_anterior ?? ''}" />
        </div>
      </div>

      <div class="pf-campo">
        <label for="pf-prod-descripcion-corta">Descripción corta</label>
        <textarea id="pf-prod-descripcion-corta" rows="2">${escaparHtml(producto?.descripcion_corta ?? '')}</textarea>
      </div>

      <div class="pf-campo">
        <label for="pf-prod-descripcion-larga">Descripción larga</label>
        <textarea id="pf-prod-descripcion-larga" rows="4">${escaparHtml(producto?.descripcion_larga ?? '')}</textarea>
      </div>

      <div class="pf-fila" style="gap: 12px;">
        <div class="pf-campo" style="flex: 1;">
          <label for="pf-prod-estado">Estado</label>
          <select id="pf-prod-estado">
            <option value="borrador" ${!producto || producto.estado === 'borrador' ? 'selected' : ''}>Borrador</option>
            <option value="publicado" ${producto?.estado === 'publicado' ? 'selected' : ''}>Publicado</option>
            <option value="archivado" ${producto?.estado === 'archivado' ? 'selected' : ''}>Archivado</option>
          </select>
        </div>
      </div>

      <div class="pf-fila" style="gap: 16px; flex-wrap: wrap;">
        <label><input type="checkbox" id="pf-prod-destacado" ${producto?.es_destacado ? 'checked' : ''} /> Destacado</label>
        ${
          tieneEtiquetas
            ? `
        <label><input type="checkbox" id="pf-prod-nuevo" ${producto?.es_nuevo ? 'checked' : ''} /> Nuevo</label>
        <label><input type="checkbox" id="pf-prod-oferta" ${producto?.es_oferta ? 'checked' : ''} /> En oferta</label>
        `
            : `
        <span style="color: var(--pf-color-texto-suave); font-size: 0.85rem; display: inline-flex; align-items: center; gap: 4px;">Nuevo / En oferta <span style="color: var(--pf-color-premium); display: inline-flex; cursor: help;" title="Disponible desde el plan Básico">${iconoCandado()}</span></span>
        `
        }
      </div>
      ${
        !tieneEtiquetas
          ? `
      <p class="pf-texto-suave" style="margin: -8px 0 12px;">
        Las etiquetas <strong>Oferta</strong> y <strong>Nuevo</strong> están disponibles desde el plan Básico.
        <a href="#/planes" style="color: var(--pf-color-primario); font-weight: 700;">Mejorar plan</a>
      </p>
      `
          : ''
      }

      <hr style="border: none; border-top: 1px solid var(--pf-color-borde); margin: 16px 0;" />
      <p class="pf-texto-suave" style="margin: 0 0 8px;">Categorías</p>
      <div class="pf-fila" style="flex-wrap: wrap; gap: 12px;">
        ${
          (categorias ?? [])
            .map(
              (c) => `
          <label style="display:flex;align-items:center;gap:6px;">
            <input type="checkbox" class="pf-prod-categoria" value="${c.id}" ${categoriaIdsAsignadas.has(c.id) ? 'checked' : ''} />
            ${c.nombre}
          </label>`,
            )
            .join('') ||
          '<p class="pf-texto-suave">Crea categorías primero para poder asignarlas.</p>'
        }
      </div>
      ${
        categorias?.length
          ? `<div class="pf-campo" style="margin-top: 8px;">
              <label for="pf-prod-categoria-principal">Categoría principal</label>
              <select id="pf-prod-categoria-principal">
                <option value="">— Ninguna —</option>
                ${categorias
                  .map(
                    (c) =>
                      `<option value="${c.id}" ${categoriaPrincipalId === c.id ? 'selected' : ''}>${c.nombre}</option>`,
                  )
                  .join('')}
              </select>
            </div>`
          : ''
      }

      <hr style="border: none; border-top: 1px solid var(--pf-color-borde); margin: 16px 0;" />
      <p class="pf-texto-suave" style="margin: 0 0 8px;">SEO (opcional)</p>

      <div class="pf-campo">
        <label for="pf-prod-seo-titulo">Meta título</label>
        <input type="text" id="pf-prod-seo-titulo" value="${producto?.seo?.meta_titulo ?? ''}" />
      </div>

      <div class="pf-campo">
        <label for="pf-prod-seo-descripcion">Meta descripción</label>
        <textarea id="pf-prod-seo-descripcion" rows="2">${producto?.seo?.meta_descripcion ?? ''}</textarea>
      </div>

      ${producto ? '' : '<p class="pf-texto-suave">Guarda el producto para poder agregarle imágenes.</p>'}

      <p class="pf-campo__error" id="pf-prod-error" hidden></p>

      <div class="pf-fila" style="justify-content: space-between; align-items: center; gap: 12px;">
        <span class="pf-texto-suave" id="pf-prod-borrador-estado" style="font-size: 12px;"></span>
        <button type="submit" class="pf-boton pf-boton--primario" id="pf-prod-submit">
          ${producto ? 'Guardar cambios' : 'Crear producto'}
        </button>
      </div>
    </form>

    ${producto ? '<div id="pf-prod-imagenes"></div>' : ''}
  `;

  if (producto)
    montarSeccionImagenes(modal.querySelector('#pf-prod-imagenes'), producto, modal, alGuardar);

  const form = modal.querySelector('#pf-form-producto');
  const campoNombre = modal.querySelector('#pf-prod-nombre');
  const campoSlug = modal.querySelector('#pf-prod-slug');
  const errorEl = modal.querySelector('#pf-prod-error');
  const submitEl = modal.querySelector('#pf-prod-submit');

  let slugTocadoManualmente = Boolean(producto);
  campoSlug.addEventListener('input', () => (slugTocadoManualmente = true));
  campoNombre.addEventListener('input', () => {
    if (!slugTocadoManualmente) campoSlug.value = generarSlugSugerido(campoNombre.value);
  });

  const gestorBorrador = crearGestorBorradorFormulario('formulario-producto', {
    contexto: producto ? `editar:${producto.id}` : 'nuevo',
    alEstado: (estado) =>
      aplicarEstadoBorrador(modal.querySelector('#pf-prod-borrador-estado'), estado),
  });

  const borrador = conectarBorradorDeFormulario({
    contenedor: form,
    modal,
    gestor: gestorBorrador,
    leerDatos: () => leerDatosFormularioProducto(form),
    aplicarDatos: (datos) => {
      aplicarDatosFormularioProducto(form, datos);
      // Un slug restaurado se considera "tocado": no se debe regenerar solo
      // al cambiar el nombre por encima del borrador del usuario.
      slugTocadoManualmente = Boolean(datos.slug);
    },
  });

  const avisoBorrador = modal.querySelector('#pf-prod-borrador-aviso');
  if (borrador.restaurado && avisoBorrador) avisoBorrador.hidden = false;

  const btnDescartarBorrador = modal.querySelector('#pf-prod-borrador-descartar');
  if (btnDescartarBorrador) {
    btnDescartarBorrador.addEventListener('click', () => {
      borrador.descartar();
      if (avisoBorrador) avisoBorrador.hidden = true;
      aplicarEstadoBorrador(modal.querySelector('#pf-prod-borrador-estado'), null);
    });
  }

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;

    const precio = Number(modal.querySelector('#pf-prod-precio').value);
    const precioAnteriorRaw = modal.querySelector('#pf-prod-precio-anterior').value;

    const datos = {
      nombre: campoNombre.value.trim(),
      slug: campoSlug.value.trim().toLowerCase(),
      precio,
      precio_anterior: precioAnteriorRaw ? Number(precioAnteriorRaw) : null,
      descripcion_corta: modal.querySelector('#pf-prod-descripcion-corta').value.trim() || null,
      descripcion_larga: modal.querySelector('#pf-prod-descripcion-larga').value.trim() || null,
      estado: modal.querySelector('#pf-prod-estado').value,
      es_destacado: modal.querySelector('#pf-prod-destacado').checked,
      es_nuevo: modal.querySelector('#pf-prod-nuevo')?.checked ?? producto?.es_nuevo ?? false,
      es_oferta: modal.querySelector('#pf-prod-oferta')?.checked ?? producto?.es_oferta ?? false,
    };

    const errores = [];
    if (datos.nombre.length < 2) errores.push('El nombre es obligatorio.');
    if (!esSlugValido(datos.slug)) errores.push('El slug no tiene un formato válido.');
    if (!(datos.precio >= 0)) errores.push('El precio debe ser un número válido.');
    if (datos.precio_anterior !== null && datos.precio_anterior <= datos.precio) {
      errores.push('El precio anterior debe ser mayor al precio actual.');
    }

    if (errores.length > 0) {
      errorEl.textContent = errores.join(' ');
      errorEl.hidden = false;
      return;
    }

    submitEl.disabled = true;
    submitEl.textContent = 'Guardando…';

    const { data: guardado, error } = producto
      ? await actualizarProducto(producto.id, datos)
      : await crearProducto(datos);

    if (error) {
      errorEl.textContent = mensajeErrorAmigable(error, 'No se pudo guardar el producto.');
      errorEl.hidden = false;
      submitEl.disabled = false;
      submitEl.textContent = producto ? 'Guardar cambios' : 'Crear producto';
      return;
    }

    // El producto ya se guardó en este punto — SEO y categorías son datos
    // complementarios. Si alguno falla, se avisa igual (en vez de tragarse
    // el error en silencio) pero sin revertir ni bloquear el flujo, porque
    // el registro principal ya es válido.
    const seoId = producto?.seo_id ?? guardado.seo_id;
    const { error: errorSeo } = seoId
      ? await actualizarSeoProducto(seoId, {
          meta_titulo: modal.querySelector('#pf-prod-seo-titulo').value.trim() || null,
          meta_descripcion: modal.querySelector('#pf-prod-seo-descripcion').value.trim() || null,
        })
      : { error: null };

    const idsSeleccionados = Array.from(modal.querySelectorAll('.pf-prod-categoria:checked')).map(
      (el) => el.value,
    );
    const principalSeleccionada =
      modal.querySelector('#pf-prod-categoria-principal')?.value || null;
    const { error: errorCategorias } = await asignarCategoriasProducto(
      guardado.id,
      idsSeleccionados.map((id) => ({
        categoriaId: id,
        esPrincipal: id === principalSeleccionada,
      })),
    );

    if (errorSeo || errorCategorias) {
      mostrarToast(
        'Producto guardado, pero el SEO o las categorías no se pudieron actualizar. Vuelve a intentarlo.',
        { tono: 'peligro' },
      );
    } else {
      mostrarToast(producto ? 'Producto actualizado.' : 'Producto creado.', { tono: 'exito' });
    }

    // El producto ya está guardado: el borrador dejó de ser necesario.
    borrador.finalizar();

    if (producto) {
      modal.cerrar();
      await alGuardar();
    } else {
      // Recarga en modo edición sobre el producto recién creado — ver nota
      // de cabecera del archivo.
      modal.setAttribute('titulo', `Editar: ${datos.nombre}`);
      await montarFormulario(modal, guardado.id, alGuardar);
      await alGuardar();
    }
  });
}

function montarSeccionImagenes(destino, producto, modal, alGuardar) {
  destino.innerHTML = `
    <hr style="border: none; border-top: 1px solid var(--pf-color-borde); margin: 16px 0;" />
    <p class="pf-texto-suave" style="margin: 0 0 8px;">Imágenes</p>
    <div class="pf-fila" id="pf-prod-imagenes-lista" style="flex-wrap: wrap; gap: 10px; margin-bottom: 10px;"></div>
    <label for="pf-prod-imagen-input">Agregar imagen</label>
    <input type="file" id="pf-prod-imagen-input" accept="image/jpeg,image/png,image/webp" />
  `;

  pintarImagenes(destino, producto, alGuardar);

  destino.querySelector('#pf-prod-imagen-input').addEventListener('change', async (evento) => {
    const archivo = evento.target.files?.[0];
    if (!archivo) return;

    const { empresa_id: empresaId } = obtenerEstadoSesion().claims;
    const { error } = await agregarImagenProducto(empresaId, producto.id, archivo, {
      esPrincipal: (producto.imagenes ?? []).length === 0,
    });

    evento.target.value = '';

    if (error) {
      // error.message acá es controlado por almacenamiento.js (validación de
      // extensión), no un error crudo de Postgres/Storage — se muestra tal
      // cual porque ya está redactado para el usuario final.
      mostrarToast(error.message ?? 'No se pudo subir la imagen.', { tono: 'peligro' });
      return;
    }

    mostrarToast('Imagen agregada.', { tono: 'exito' });
    const { data: actualizado } = await obtenerProducto(producto.id);
    if (actualizado) {
      producto.imagenes = actualizado.imagenes;
      pintarImagenes(destino, producto, alGuardar);
    }
  });
}

function pintarImagenes(destino, producto, alGuardar) {
  const lista = destino.querySelector('#pf-prod-imagenes-lista');
  lista.innerHTML = '';

  for (const imagen of producto.imagenes ?? []) {
    const tarjeta = document.createElement('div');
    tarjeta.style.cssText = 'width:90px;text-align:center;';

    const img = document.createElement('img');
    img.src = imagen.url;
    img.alt = imagen.texto_alternativo ?? '';
    img.style.cssText = `width:90px;height:90px;object-fit:cover;border-radius:8px;border:2px solid ${
      imagen.es_principal ? 'var(--pf-color-primario, #2563eb)' : 'transparent'
    };`;

    const acciones = document.createElement('div');
    acciones.style.cssText = 'display:flex;gap:4px;justify-content:center;margin-top:4px;';

    if (!imagen.es_principal) {
      const botonPrincipal = document.createElement('button');
      botonPrincipal.type = 'button';
      botonPrincipal.className = 'pf-boton pf-boton--secundario pf-boton--chico';
      botonPrincipal.textContent = 'Principal';
      botonPrincipal.addEventListener('click', async () => {
        botonPrincipal.disabled = true;
        const { error } = await marcarImagenPrincipal(producto.id, imagen.id);
        if (error) {
          mostrarToast('No se pudo marcar la imagen como principal.', { tono: 'peligro' });
          botonPrincipal.disabled = false;
          return;
        }
        const { data: actualizado } = await obtenerProducto(producto.id);
        producto.imagenes = actualizado.imagenes;
        pintarImagenes(destino, producto, alGuardar);
      });
      acciones.appendChild(botonPrincipal);
    }

    const botonBorrar = document.createElement('button');
    botonBorrar.type = 'button';
    botonBorrar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
    botonBorrar.innerHTML = iconoEliminar();
    botonBorrar.title = 'Eliminar imagen';
    botonBorrar.addEventListener('click', async () => {
      botonBorrar.disabled = true;
      const { error } = await eliminarImagenProducto(imagen.id);
      if (error) {
        mostrarToast('No se pudo eliminar la imagen.', { tono: 'peligro' });
        botonBorrar.disabled = false;
        return;
      }
      const { data: actualizado } = await obtenerProducto(producto.id);
      producto.imagenes = actualizado.imagenes;
      pintarImagenes(destino, producto, alGuardar);
    });
    acciones.appendChild(botonBorrar);

    tarjeta.append(img, acciones);
    lista.appendChild(tarjeta);
  }
}

async function abrirModalRelacionados(modal, producto) {
  modal.setAttribute('titulo', `Productos relacionados con: ${producto.nombre}`);
  modal.innerHTML = `
    <p class="pf-texto-suave" style="margin-top:0; margin-bottom:16px;">
      Selecciona los productos que quieres recomendar cuando un cliente vea <strong>${producto.nombre}</strong>.
    </p>

    <div style="margin-bottom: 24px;">
      <h3 style="margin-top:0; margin-bottom:8px;">Relacionados actuales</h3>
      <div id="pf-relacionados-actuales"><p class="pf-texto-suave">Cargando relacionados…</p></div>
    </div>

    <hr style="border:none; border-top:1px solid var(--pf-color-borde, #eee); margin:20px 0;" />

    <div>
      <h3 style="margin-top:0; margin-bottom:8px;">Agregar productos a recomendar</h3>
      <div class="pf-campo" style="margin-bottom: 12px;">
        <label for="pf-rel-busqueda">Buscar en tu catálogo</label>
        <input type="search" id="pf-rel-busqueda" placeholder="Nombre del producto…" />
      </div>
      <div id="pf-rel-busqueda-resultados"><p class="pf-texto-suave">Ingresa un término para buscar productos.</p></div>
    </div>
  `;

  async function recargarRelacionadosActuales() {
    const contenedorActuales = modal.querySelector('#pf-relacionados-actuales');
    if (!contenedorActuales) return new Set();

    const { data: relaciones, error } = await listarProductosRelacionados(producto.id);
    if (error) {
      contenedorActuales.innerHTML = `<p class="pf-campo__error">${mensajeErrorAmigable(error)}</p>`;
      return new Set();
    }

    const idsRelacionados = new Set((relaciones ?? []).map((r) => r.producto_relacionado_id));

    if (!relaciones || relaciones.length === 0) {
      contenedorActuales.innerHTML =
        '<p class="pf-texto-suave">No hay productos relacionados asignados aún.</p>';
      return idsRelacionados;
    }

    const lista = document.createElement('div');
    lista.style.cssText = 'display:flex; flex-direction:column; gap:8px;';

    for (const rel of relaciones) {
      const pRel = rel.relacionado;
      if (!pRel) continue;

      const fila = document.createElement('div');
      fila.className = 'pf-fila pf-fila--separada';
      fila.style.cssText =
        'padding:8px 12px; background:var(--pf-color-fondo-alterno, #f9fafb); border-radius:6px; border:1px solid var(--pf-color-borde, #e5e7eb); align-items:center;';

      const info = document.createElement('div');
      info.style.cssText = 'display:flex; align-items:center; gap:10px;';

      const imgPrincipal = pRel.imagenes?.find((i) => i.es_principal) ?? pRel.imagenes?.[0];
      if (imgPrincipal) {
        const img = document.createElement('img');
        img.src = imgPrincipal.url;
        img.alt = '';
        img.style.cssText = 'width:36px; height:36px; object-fit:cover; border-radius:4px;';
        info.appendChild(img);
      }

      const texto = document.createElement('div');
      texto.innerHTML = `<strong>${pRel.nombre}</strong> <span class="pf-texto-suave">(S/ ${Number(pRel.precio ?? 0).toFixed(2)})</span>`;
      info.appendChild(texto);

      const btnQuitar = document.createElement('button');
      btnQuitar.type = 'button';
      btnQuitar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
      btnQuitar.textContent = 'Quitar';
      btnQuitar.addEventListener('click', async () => {
        btnQuitar.disabled = true;
        const { error: errDel } = await eliminarProductoRelacionado(producto.id, pRel.id);
        if (errDel) {
          mostrarToast(mensajeErrorAmigable(errDel), { tono: 'peligro' });
          btnQuitar.disabled = false;
          return;
        }
        mostrarToast('Relación eliminada.', { tono: 'exito' });
        const nuevosIds = await recargarRelacionadosActuales();
        const inputBusqueda = modal.querySelector('#pf-rel-busqueda');
        if (inputBusqueda && inputBusqueda.value.trim()) {
          await ejecutarBusqueda(inputBusqueda.value.trim(), nuevosIds);
        }
      });

      fila.appendChild(info);
      fila.appendChild(btnQuitar);
      lista.appendChild(fila);
    }

    contenedorActuales.innerHTML = '';
    contenedorActuales.appendChild(lista);
    return idsRelacionados;
  }

  async function ejecutarBusqueda(query, idsRelacionados) {
    const contenedorResultados = modal.querySelector('#pf-rel-busqueda-resultados');
    if (!contenedorResultados) return;

    if (!query) {
      contenedorResultados.innerHTML =
        '<p class="pf-texto-suave">Ingresa un término para buscar productos.</p>';
      return;
    }

    contenedorResultados.innerHTML = '<p class="pf-texto-suave">Buscando…</p>';
    const { data: resultados, error } = await listarProductos({ busqueda: query });

    if (error) {
      contenedorResultados.innerHTML = `<p class="pf-campo__error">${mensajeErrorAmigable(error)}</p>`;
      return;
    }

    const candidatos = (resultados ?? []).filter((p) => p.id !== producto.id);

    if (candidatos.length === 0) {
      contenedorResultados.innerHTML =
        '<p class="pf-texto-suave">No se encontraron productos coincidentes.</p>';
      return;
    }

    const lista = document.createElement('div');
    lista.style.cssText = 'display:flex; flex-direction:column; gap:8px; margin-top:8px;';

    for (const cand of candidatos) {
      const yaRelacionado = idsRelacionados.has(cand.id);

      const fila = document.createElement('div');
      fila.className = 'pf-fila pf-fila--separada';
      fila.style.cssText =
        'padding:8px 12px; border-radius:6px; border:1px solid var(--pf-color-borde, #e5e7eb); align-items:center;';

      const info = document.createElement('div');
      info.style.cssText = 'display:flex; align-items:center; gap:10px;';

      const imgPrincipal = cand.imagenes?.find((i) => i.es_principal) ?? cand.imagenes?.[0];
      if (imgPrincipal) {
        const img = document.createElement('img');
        img.src = imgPrincipal.url;
        img.alt = '';
        img.style.cssText = 'width:36px; height:36px; object-fit:cover; border-radius:4px;';
        info.appendChild(img);
      }

      const texto = document.createElement('div');
      texto.innerHTML = `<strong>${escaparHtml(cand.nombre)}</strong> <span class="pf-texto-suave">(S/ ${Number(cand.precio ?? 0).toFixed(2)})</span>`;
      info.appendChild(texto);

      const btnAgregar = document.createElement('button');
      btnAgregar.type = 'button';
      btnAgregar.className = yaRelacionado
        ? 'pf-boton pf-boton--secundario pf-boton--chico'
        : 'pf-boton pf-boton--primario pf-boton--chico';
      btnAgregar.textContent = yaRelacionado ? 'Ya relacionado' : '+ Agregar';
      btnAgregar.disabled = yaRelacionado;

      if (!yaRelacionado) {
        btnAgregar.addEventListener('click', async () => {
          btnAgregar.disabled = true;
          const { error: errAdd } = await agregarProductoRelacionado(producto.id, cand.id);
          if (errAdd) {
            mostrarToast(mensajeErrorAmigable(errAdd), { tono: 'peligro' });
            btnAgregar.disabled = false;
            return;
          }
          mostrarToast('Producto relacionado agregado.', { tono: 'exito' });
          const nuevosIds = await recargarRelacionadosActuales();
          await ejecutarBusqueda(query, nuevosIds);
        });
      }

      fila.appendChild(info);
      fila.appendChild(btnAgregar);
      lista.appendChild(fila);
    }

    contenedorResultados.innerHTML = '';
    contenedorResultados.appendChild(lista);
  }

  await recargarRelacionadosActuales();

  const inputBusqueda = modal.querySelector('#pf-rel-busqueda');
  if (inputBusqueda) {
    inputBusqueda.addEventListener(
      'input',
      debounce(async (e) => {
        const query = e.target.value.trim();
        const { data: relaciones } = await listarProductosRelacionados(producto.id);
        const ids = new Set((relaciones ?? []).map((r) => r.producto_relacionado_id));
        await ejecutarBusqueda(query, ids);
      }, 300),
    );
  }
}
